import { MenuItem as HeadlessMenuItem } from "@headlessui/react";
import Link from "next/link";
import type React from "react";
import { forwardRef } from "react";
import { AccessibleIcon } from "../../accessibility/AccessibleIcon";
import { classNames } from "../../utils";

type SvgIconComponent = (props: {
	className?: string;
	title?: string;
	titleId?: string;
}) => React.ReactNode;

export type MenuItemOption = {
	label: string;
	icon?: SvgIconComponent;
	rightIcon?: SvgIconComponent;
	rightIconLabel?: string;
	description?: string;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	href?: string;
	prefetch?: boolean;
	target?: React.HTMLAttributeAnchorTarget;
	disabled?: boolean;
	className?: string;
	isDestructive?: boolean;
};

const MenuItemInner = (props: MenuItemOption, ref: React.ForwardedRef<HTMLButtonElement>) => {
	const { label, description, onClick, href, prefetch, target, disabled, className, isDestructive } = props;

	if (href) {
		return (
			<HeadlessMenuItem as={Link} href={href} prefetch={prefetch} target={target}>
				{() => (
					<button
						type="button"
						disabled={disabled}
						ref={ref}
						onClick={onClick}
						className={classNames(
							"flex w-full justify-between rounded-md px-2 py-1 text-left text-neutral-faded text-sm hover:bg-neutral-faded",
							isDestructive ? "text-critical" : "",
							disabled ? "cursor-not-allowed opacity-50 hover:bg-transparent" : "",
							className,
						)}
					>
						<div
							className={classNames(
								"flex w-full gap-2 stroke-2",
								description ? "items-start" : "items-center",
							)}
						>
							{props.icon && <props.icon className="h-5 w-5" />}
							<div className="flex grow flex-col truncate">
								<span>{label}</span>
								{description && <span className="text-neutral-faded text-xs">{description}</span>}
							</div>
						</div>
						{props.rightIcon && (
							<AccessibleIcon label={props.rightIconLabel}>
								<props.rightIcon className="h-5 w-5" />
							</AccessibleIcon>
						)}
					</button>
				)}
			</HeadlessMenuItem>
		);
	}

	return (
		<HeadlessMenuItem>
			{() => (
				<button
					disabled={disabled}
					type="button"
					ref={ref}
					onClick={onClick}
					className={classNames(
						"flex w-full justify-between rounded-md px-2 py-1 text-left text-neutral-faded text-sm hover:bg-neutral-faded",
						isDestructive ? "text-critical" : "",
						disabled ? "cursor-not-allowed opacity-50 hover:bg-transparent" : "",
						className,
					)}
				>
					<div
						className={classNames(
							"flex w-full gap-2 stroke-2",
							description ? "items-start" : "items-center",
						)}
					>
						{props.icon && <props.icon className="h-5 w-5" />}
						<div className="flex grow flex-col truncate">
							<span>{label}</span>
							{description && <span className="text-neutral-faded text-xs">{description}</span>}
						</div>
						{props.rightIcon && (
							<AccessibleIcon label={props.rightIconLabel}>
								<props.rightIcon className="h-4 w-4" />
							</AccessibleIcon>
						)}
					</div>
				</button>
			)}
		</HeadlessMenuItem>
	);
};

export const MenuItem = forwardRef(MenuItemInner);
